<script setup lang="ts">
import { Loader2, Pencil } from 'lucide-vue-next'
import { ESection, EImage } from '@/components/base/Email'
import EmailBlockWrapper from './EmailBlockWrapper.vue'
import EmailHeaderToolbar from './EmailHeaderToolbar.vue'
import { useImageCropUpload } from '@/components/ui/ImageUploader/useImageCropUpload'
import { headerLogoStyle } from '../../utils/constants'
import type { EmailHeader } from '../../types'

const { t } = useI18n()

const props = defineProps<{
  header: EmailHeader
  isSelected: boolean
  isUploading?: boolean
  colorPresets?: { color: string; label: string }[]
}>()

const emit = defineEmits<{
  (e: 'select'): void
  (e: 'update:header', header: EmailHeader): void
  (e: 'upload-file', file: File): void
}>()

const fileInputRef = ref<HTMLInputElement | null>(null)
// Starts false: an already-cached/complete logo may never fire `load`, which would
// leave the overlay stuck. The watch below re-arms it only when the image is swapped.
const isImageLoading = ref(false)

const { cropSrc, handleFileSelected, onCropConfirm, onCropCancel } = useImageCropUpload((file) =>
  emit('upload-file', file)
)

watch(
  () => props.header.logo,
  (newSrc, oldSrc) => {
    if (newSrc && newSrc !== oldSrc) isImageLoading.value = true
  }
)

const showOverlayLoader = computed(() => props.isUploading || isImageLoading.value)

const triggerUpload = () => {
  fileInputRef.value?.click()
}
</script>

<template>
  <EmailBlockWrapper
    v-if="header.showLogo"
    :is-selected="isSelected"
    :label="t('pages.email.emailTemplateCreatePage.blockHeader')"
    cursor="pointer"
    rounded-edge="top"
    :block-background-color="header.blockBackgroundColor"
    @select="emit('select')"
  >
    <input
      ref="fileInputRef"
      type="file"
      accept="image/jpeg, image/png, image/gif, image/webp"
      class="hidden"
      @change="handleFileSelected"
    />

    <div class="group/logo relative">
      <ESection
        :style="{
          padding: '20px 0',
          textAlign: header.align
        }"
        @click="!header.logo && triggerUpload()"
      >
        <div
          v-if="header.logo"
          class="relative inline-block max-w-full"
          :style="{
            margin: header.align === 'center' ? '0 auto' : header.align === 'right' ? '0 0 0 auto' : '0'
          }"
        >
          <EImage
            :src="header.logo"
            alt="Logo"
            :style="headerLogoStyle(header.logoWidth)"
            @load="isImageLoading = false"
            @error="isImageLoading = false"
          />
          <div
            v-if="showOverlayLoader"
            class="absolute inset-0 flex items-center justify-center rounded-lg bg-white/70"
          >
            <Loader2 class="size-6 animate-spin text-primary" />
          </div>
        </div>

        <div
          v-else
          class="flex h-12 w-24 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted transition-colors hover:border-border hover:bg-accent"
          :class="{
            'mx-auto': header.align === 'center',
            'ml-auto': header.align === 'right',
            'mr-auto': header.align === 'left'
          }"
        >
          <Loader2 v-if="isUploading" class="size-5 animate-spin text-primary" />
          <span v-else class="text-xs text-muted-foreground">Logo</span>
        </div>
      </ESection>

      <div
        v-if="header.logo && !showOverlayLoader"
        class="pointer-events-none absolute inset-0 flex items-center justify-center bg-foreground/0 opacity-0 transition-all group-hover/logo:bg-foreground/5 group-hover/logo:opacity-100"
      >
        <button
          type="button"
          class="pointer-events-auto inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-foreground shadow ring-1 ring-border hover:bg-muted/50"
          @click.stop="triggerUpload"
        >
          <Pencil class="size-4" />
          {{ t('pages.email.emailTemplateCreatePage.changeImage') }}
        </button>
      </div>
    </div>

    <ImageCropDialog :src="cropSrc" @confirm="onCropConfirm" @cancel="onCropCancel" />
  </EmailBlockWrapper>

  <EmailHeaderToolbar
    v-if="isSelected"
    :header="header"
    :color-presets="colorPresets"
    @update:header="emit('update:header', $event)"
    @replace="triggerUpload"
  />
</template>
